import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthProvider';
import { FaHome, FaUsers, FaInfoCircle, FaSignOutAlt, FaCog, FaChevronDown, FaChevronUp, FaUserPlus, FaWhatsapp } from 'react-icons/fa';
import { GiReceiveMoney, GiTakeMyMoney } from 'react-icons/gi';
import { FiBookmark, FiBookOpen, FiMenu, FiShoppingBag } from 'react-icons/fi';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import i18n from '../i18n';
import logoImage from '../assets/images/payPlus/payPlusNoBackground.png';
import logoImageWithTitle from '../assets/images/payPlus/payPlusWithTitle.png';

export default function Navbar(props) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Başlangıçta kapalı
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [language, setLanguage] = useState(i18n.language || 'en');
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const themeOptions = [
    { icon: 'pi pi-sun', value: 'light' },
    { icon: 'pi pi-moon', value: 'dark' }
  ];

  const languageOptions = [
    { label: 'EN', value: 'en' },
    { label: 'TR', value: 'tr' }
  ];

  const toggleTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const handleSettingsClick = () => {
    if (!sidebarOpen) {
      setSidebarOpen(true);
      setTimeout(() => setShowSettings(true), 100);
    } else {
      setShowSettings(!showSettings);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    setSidebarOpen(false);
  };

  const handleAdminPanel = () => {
    navigate('/admins');
    setSidebarOpen(false);
  };

  return (
    <div
      className="w-screen flex bg-white dark:bg-gray-900 fixed inset-0"
      style={{
        height: '100vh',
        height: '100dvh',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        touchAction: 'pan-y',
        overscrollBehavior: 'none'
      }}
    >
      {/* Sidebar */}
      <div
        className={`transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'} bg-logo-mid dark:bg-gray-700 text-white flex flex-col flex-shrink-0 relative z-30`}
        style={{
          touchAction: 'none',
          overscrollBehavior: 'none'
        }}
      >
        {/* Logo ve Kullanıcı Bilgisi */}
        <div className="p-4 border-b border-gray-500 flex flex-col items-center flex-shrink-0">
          {/* Logo */}
          <div className="mb-3">
            <img
              src={sidebarOpen ? logoImageWithTitle : logoImage}
              alt="Logo"
              className={`${sidebarOpen
                ? 'h-12 w-auto max-w-[200px]'
                : 'h-8 w-8'
                } object-contain dark:filter dark:grayscale-[70%] transition-all duration-300`}
            />
          </div>

          {sidebarOpen && (
            <>
              <div className="font-semibold text-center">{user?.name} {user?.surname}</div>
              <div className="text-xs text-center">{user?.companyName}</div>
            </>
          )}
        </div>

        {/* Menü Header */}
        <div className={`flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'} p-2 flex-shrink-0`}>
          {sidebarOpen && <span className="font-bold text-sm">MENÜ</span>}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="focus:outline-none p-1 hover:bg-logo-dark rounded transition-colors"
          >
            <FiMenu className="text-xl" />
          </button>
        </div>

        {/* Menü Items - Scrollable */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden pb-4" style={{ scrollbarWidth: 'thin' }}>
          {/* Ana Sayfa */}
          <div
            onClick={() => handleNavigation('/homepage')}
            className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
              } py-3`}
          >
            <div className="text-xl flex-shrink-0"><FaHome /></div>
            {sidebarOpen && <span className="ml-3 truncate">Anasayfa</span>}
          </div>

          {/* Divider */}
          <div className="border-t dark:border-gray-600 border-gray-200 mx-3 my-2"></div>

          {/* Müşteri İşlemleri */}
          <div
            onClick={() => handleNavigation('/customers')}
            className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
              } py-3`}
          >
            <div className="text-xl flex-shrink-0"><FaUsers /></div>
            {sidebarOpen && <span className="ml-3 truncate">Müşteri Listesi</span>}
          </div>

          <div
            onClick={() => handleNavigation('/add-customer')}
            className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
              } py-3`}
          >
            <div className="text-xl flex-shrink-0"><FaUserPlus /></div>
            {sidebarOpen && <span className="ml-3 truncate">Yeni Müşteri</span>}
          </div>

          {/* Divider */}
          <div className="border-t dark:border-gray-600 border-gray-200 mx-3 my-2"></div>

          {/* Ödeme İşlemleri */}
          <div
            onClick={() => handleNavigation('/pay-list')}
            className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
              } py-3`}
          >
            <div className="text-xl flex-shrink-0"><GiTakeMyMoney /></div>
            {sidebarOpen && <span className="ml-3 truncate">Ödeme Listesi</span>}
          </div>

          <div
            onClick={() => handleNavigation('/add-pay')}
            className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
              } py-3`}
          >
            <div className="text-xl flex-shrink-0"><GiReceiveMoney /></div>
            {sidebarOpen && <span className="ml-3 truncate">Yeni Ödeme</span>}
          </div>

          {/* Divider */}
          <div className="border-t dark:border-gray-600 border-gray-200 mx-3 my-2"></div>

          {/* Diğer */}
          <div
            onClick={() => handleNavigation('/pricing')}
            className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
              } py-3`}
          >
            <div className="text-xl flex-shrink-0"><FiShoppingBag /></div>
            {sidebarOpen && <span className="ml-3 truncate">Fiyat Listesi</span>}
          </div>

          <div
            onClick={() => handleNavigation('/user-manual')}
            className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
              } py-3`}
          >
            <div className="text-xl flex-shrink-0"><FiBookOpen /></div>
            {sidebarOpen && <span className="ml-3 truncate">Kullanım Rehberi</span>}
          </div>

          {/* <div
            onClick={() => handleNavigation('/support')}
            className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
              } py-3`}
          >
            <div className="text-xl flex-shrink-0"><FaHeadset /></div>
            {sidebarOpen && <span className="ml-3 truncate">Destek</span>}
          </div> */}

          <div
            onClick={() => window.open(`https://wa.me/905325999867`, '_blank')}
            className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
              } py-3`}
          >
            <div className="text-xl flex-shrink-0"><FaWhatsapp /></div>
            {sidebarOpen && <span className="ml-3 truncate">Whatsapp</span>}
          </div>

          {/* <div
            onClick={() => handleNavigation('/about')}
            className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
              } py-3`}
          >
            <div className="text-xl flex-shrink-0"><FaInfoCircle /></div>
            {sidebarOpen && <span className="ml-3 truncate">Hakkımızda</span>}
          </div> */}
        </div>

        {/* Ayarlar Bölümü */}
        <div className="flex-shrink-0">
          <div className="border-t dark:border-gray-600 border-gray-200 mx-3 my-2"></div>

          <div>
            <div
              onClick={handleSettingsClick}
              className={`flex items-center cursor-pointer hover:bg-logo-dark transition-colors ${sidebarOpen ? 'px-4 justify-start' : 'px-2 justify-center'
                } py-3`}
            >
              <div className="text-xl flex-shrink-0"><FaCog /></div>
              {sidebarOpen && (
                <>
                  <span className="ml-3">Ayarlar</span>
                  <div className="ml-auto mr-1">
                    {showSettings ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </>
              )}
            </div>

            {/* Ayarlar İçeriği */}
            {showSettings && sidebarOpen && (
              <div className="transition-all duration-200 p-3 bg-logo-dark bg-opacity-50 mx-2 rounded">
                <div className="flex flex-col space-y-3">
                  {/* Tema */}
                  <div>
                    <div className="text-xs mb-2 font-medium">Tema</div>
                    <div className="flex rounded-md overflow-hidden border border-gray-500">
                      {themeOptions.map((option) => (
                        <div
                          key={option.value}
                          className={`flex-1 flex items-center justify-center p-2 cursor-pointer transition-all
                            ${theme === option.value
                              ? 'bg-logo-light text-gray-800'
                              : 'bg-gray-600 text-white hover:bg-gray-500'
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTheme(option.value);
                          }}
                        >
                          <i className={`${option.icon} text-sm`}></i>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Dil */}
                  {/* <div>
                    <div className="text-xs mb-2 font-medium">Dil</div>
                    <SelectButton
                      value={language}
                      options={languageOptions}
                      onChange={(e) => {
                        e.originalEvent?.stopPropagation();
                        changeLanguage(e.value);
                      }}
                      className="w-full"
                      pt={{
                        button: ({ context }) => ({
                          className: `${context.selected
                            ? 'bg-logo-light text-gray-800'
                            : 'bg-gray-600 text-white hover:bg-gray-500'
                            } text-xs py-2 flex-1 border-0`
                        }),
                        root: { className: 'flex rounded-md overflow-hidden border border-gray-500' }
                      }}
                    />
                  </div> */}

                  <div>
                    <Button
                      label="Profil"
                      icon="pi pi-user"
                      className="w-full p-2 bg-logo-light text-gray-800 rounded border-0 hover:bg-opacity-80"
                      onClick={() => handleNavigation(`/profile/${user?.username}`)}
                    />
                  </div>

                  {/* Admin Panel */}
                  {user?.role === 'ROLE_ADMIN' && (
                    <div>
                      <Button
                        label="Yönetici Paneli"
                        icon="pi pi-crown"
                        className="w-full p-2 bg-logo-light text-gray-800 rounded border-0 hover:bg-opacity-80"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAdminPanel();
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Çıkış - Safari bottom bar için padding */}
        <div className="p-4 border-t border-gray-500 flex-shrink-0 pb-8 mb-5" style={{ paddingBottom: 'env(safe-area-inset-bottom, 32px)' }}>
          <button
            onClick={handleLogout}
            className={`w-full bg-red-500 hover:bg-red-600 py-2 rounded flex items-center transition-colors justify-center`}
          >
            <FaSignOutAlt className={sidebarOpen ? "mr-2" : ""} />
            {sidebarOpen && 'Çıkış'}
          </button>
        </div>
      </div>

      {/* Sağ İçerik */}
      <div className="flex-1 overflow-auto bg-white dark:bg-gray-900 relative z-20">
        {/* Mobile Overlay - Blur sadece content için */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[25]"
            style={{
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)'
            }}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div
          className="p-2 h-full relative z-10"
          style={{
            paddingBottom: 'env(safe-area-inset-bottom, 16px)'
          }}
        >
          {props.item}
        </div>
      </div>
    </div>
  );
}